import React, { useState, useEffect } from 'react';
import { XMarkIcon, DocumentDuplicateIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { HAProxyTemplate } from '../../types';

// Import Ace Editor
import AceEditor from 'react-ace';

// Import Ace base modules directly
import ace from 'ace-builds';
import 'ace-builds/src-noconflict/mode-text';
import 'ace-builds/src-noconflict/theme-monokai';
import 'ace-builds/src-noconflict/ext-language_tools';
import 'ace-builds/src-noconflict/ext-searchbox';

// Import our custom HAProxy mode
import './HAProxyMode';

// Set Ace configuration
ace.config.set('basePath', '/node_modules/ace-builds/src-noconflict');
ace.config.set('modePath', '/node_modules/ace-builds/src-noconflict');
ace.config.set('themePath', '/node_modules/ace-builds/src-noconflict');
ace.config.set('workerPath', '/node_modules/ace-builds/src-noconflict');

interface TemplateEditorProps {
  template: HAProxyTemplate | null;
  onSave: (template: Omit<HAProxyTemplate, 'id' | 'createdAt' | 'updatedAt' | 'lastUsedAt'>) => void;
  onCancel: () => void;
}

const TemplateEditor: React.FC<TemplateEditorProps> = ({ template, onSave, onCancel }) => {
  const [formData, setFormData] = useState<{
    name: string;
    description: string;
    content: string;
    isDefault: boolean;
  }>({
    name: '',
    description: '',
    content: `# Global settings
global
    log /dev/log local0
    maxconn 4096
    user haproxy
    group haproxy
    daemon

# Default settings
defaults
    log     global
    mode    http
    option  httplog
    timeout connect 5000ms
    timeout client  50000ms
    timeout server  50000ms

# Frontend definitions
frontend main
    bind *:80
    default_backend web_servers

# MANAGED BACKENDS - DO NOT EDIT THIS SECTION MANUALLY
{{BACKENDS}}`,
    isDefault: false
  });

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name,
        description: template.description || '',
        content: template.content,
        isDefault: template.isDefault
      });
    }
  }, [template]);

  // Create HAProxy custom mode
  useEffect(() => {
    // Custom styling has been applied
    // The HAProxy mode is loaded via the import above
    
    // Test that the mode is available
    try {
      // @ts-ignore
      const haproxyMode = ace.require('ace/mode/haproxy');
      console.log('HAProxy mode available:', !!haproxyMode);
    } catch (e) {
      console.error('Error checking for HAProxy mode:', e);
    }
  }, []);

  // Apply custom styling for HAProxy keywords
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      /* Base editor styling */
      .ace-monokai {
        background-color: #18181b !important;
      }
      .ace-monokai .ace_gutter {
        background-color: #27272a !important;
        color: #71717a !important;
      }
      .ace-monokai .ace_marker-layer .ace_active-line {
        background-color: #27272a !important;
      }
      .ace-monokai .ace_gutter-active-line {
        background-color: #3f3f46 !important;
      }

      /* Comments styling - make them clearly gray */
      .ace-monokai .ace_comment {
        color: #6b7280 !important;
        font-style: italic !important;
      }

      /* Section headers - make them blue and bold */
      .ace-monokai .ace_keyword.ace_section {
        color: #60a5fa !important;
        font-weight: bold !important;
      }

      /* Directives - make them green */
      .ace-monokai .ace_keyword.ace_directive {
        color: #34d399 !important;
        font-weight: 500 !important;
      }

      /* Placeholder highlighting */
      .ace-monokai .ace_string.ace_placeholder {
        color: #f97316 !important;
        font-weight: bold !important;
        background-color: rgba(249, 115, 22, 0.1) !important;
        border-radius: 2px !important;
      }
      
      /* Constants and numbers */
      .ace-monokai .ace_constant {
        color: #c084fc !important;
      }
      
      /* Strings */
      .ace-monokai .ace_string {
        color: #c084fc !important;
      }
    `;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  const [showShortcuts, setShowShortcuts] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleEditorChange = (newValue: string) => {
    setFormData(prev => ({
      ...prev,
      content: newValue
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert('Template name is required');
      return;
    }

    if (!formData.content.trim()) {
      alert('Template content is required');
      return;
    }

    if (!formData.content.includes('{{BACKENDS}}')) {
      alert('Template must include {{BACKENDS}} placeholder');
      return;
    }

    onSave(formData);
  };

  // ACE editor options
  const editorOptions = {
    enableBasicAutocompletion: true,
    enableLiveAutocompletion: true,
    enableSnippets: true,
    showLineNumbers: true,
    tabSize: 4,
    highlightActiveLine: true,
    useWorker: false
  };

  // Custom HAProxy completions
  const haproxyCompletions = [
    { name: 'global', value: 'global', meta: 'section' },
    { name: 'defaults', value: 'defaults', meta: 'section' },
    { name: 'frontend', value: 'frontend', meta: 'section' },
    { name: 'backend', value: 'backend', meta: 'section' },
    { name: 'listen', value: 'listen', meta: 'section' },
    { name: 'bind', value: 'bind', meta: 'keyword' },
    { name: 'mode', value: 'mode', meta: 'keyword' },
    { name: 'option', value: 'option', meta: 'keyword' },
    { name: 'balance', value: 'balance', meta: 'keyword' },
    { name: 'server', value: 'server', meta: 'keyword' },
    { name: 'timeout', value: 'timeout', meta: 'keyword' },
    { name: 'maxconn', value: 'maxconn', meta: 'keyword' },
    { name: 'user', value: 'user', meta: 'keyword' },
    { name: 'group', value: 'group', meta: 'keyword' },
    { name: 'daemon', value: 'daemon', meta: 'keyword' },
    { name: 'log', value: 'log', meta: 'keyword' },
    { name: 'use_backend', value: 'use_backend', meta: 'keyword' },
    { name: 'default_backend', value: 'default_backend', meta: 'keyword' },
    { name: 'acl', value: 'acl', meta: 'keyword' },
    { name: 'http-request', value: 'http-request', meta: 'keyword' },
    { name: 'http-response', value: 'http-response', meta: 'keyword' },
    { name: '{{BACKENDS}}', value: '{{BACKENDS}}', meta: 'placeholder' },
    { name: 'mode http', value: 'mode http', meta: 'directive' },
    { name: 'mode tcp', value: 'mode tcp', meta: 'directive' },
    { name: 'balance roundrobin', value: 'balance roundrobin', meta: 'directive' },
    { name: 'balance leastconn', value: 'balance leastconn', meta: 'directive' },
    { name: 'option httplog', value: 'option httplog', meta: 'directive' },
    { name: 'option tcplog', value: 'option tcplog', meta: 'directive' },
    { name: 'option http-server-close', value: 'option http-server-close', meta: 'directive' },
    { name: 'option forwardfor', value: 'option forwardfor', meta: 'directive' },
    { name: 'option httpchk', value: 'option httpchk', meta: 'directive' },
  ];

  // Register completions
  useEffect(() => {
    try {
      const langTools = ace.require('ace/ext/language_tools');
      const haproxyCompleter = {
        getCompletions: (_editor: any, _session: any, _pos: any, _prefix: any, callback: (err: Error | null, completions: any[]) => void) => {
          callback(null, haproxyCompletions);
        }
      };
      
      // Register custom completer
      langTools.setCompleters([langTools.snippetCompleter, langTools.keyWordCompleter, haproxyCompleter]);
    } catch (e) {
      console.error('Failed to register custom completions:', e);
    }
  }, []);

  // Custom styles to match app theme
  const customStyles = {
    editor: {
      borderRadius: '0.375rem',
    },
    editorContainer: {
      position: 'relative' as const,
      backgroundColor: '#18181b',
      borderRadius: '0.375rem',
      height: '500px',
      overflow: 'hidden'
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div className="fixed inset-0 bg-zinc-950 bg-opacity-75 backdrop-blur-sm transition-opacity" onClick={onCancel}></div>
      <div className="bg-zinc-900 rounded-xl shadow-2xl overflow-hidden w-full max-w-5xl relative z-10 max-h-[90vh] flex flex-col">
        <div className="border-b border-zinc-800 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <DocumentDuplicateIcon className="h-5 w-5 text-indigo-500" />
              <h2 className="text-lg font-medium text-zinc-100">
                {template ? 'Edit Template' : 'Create New Template'}
              </h2>
            </div>
            <button 
              type="button"
              onClick={onCancel}
              className="rounded-md bg-transparent text-zinc-400 hover:text-zinc-200 focus:outline-none"
            >
              <span className="sr-only">Close</span>
              <XMarkIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col h-full">
          <div className="p-6 space-y-6 flex-grow overflow-y-auto">
            {/* Template name and default checkbox */}
            <div className="flex items-center justify-between">
              <div className="flex-grow mr-6">
                <label htmlFor="name" className="block text-sm font-medium text-zinc-400 mb-1">
                  Template Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="block w-full rounded-md bg-zinc-700/50 border border-zinc-600 py-2 px-3 text-sm text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                  placeholder="e.g. Production Configuration"
                  required
                />
              </div>
              <div className="flex items-center self-end pb-1">
                <input
                  id="isDefault"
                  name="isDefault"
                  type="checkbox"
                  checked={formData.isDefault}
                  onChange={handleChange}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-zinc-600 rounded"
                />
                <label htmlFor="isDefault" className="ml-2 block text-sm text-zinc-400">
                  Set as default template
                </label>
              </div>
            </div>

            {/* Template editor with info icon */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center">
                  <label htmlFor="content" className="block text-sm font-medium text-zinc-400">
                    Template Content
                  </label>
                  <div className="relative ml-2">
                    <InformationCircleIcon 
                      className="h-4 w-4 text-zinc-500 hover:text-zinc-300 cursor-pointer"
                      onClick={() => setShowShortcuts(!showShortcuts)}
                    />
                    {showShortcuts && (
                      <div className="absolute left-6 top-0 mt-1 bg-zinc-800 rounded shadow-lg p-2 z-10 text-xs text-zinc-300 w-64">
                        <p className="font-semibold mb-1">Keyboard Shortcuts:</p>
                        <ul className="space-y-1">
                          <li className="flex items-center">
                            <kbd className="px-1.5 py-0.5 bg-zinc-700 rounded text-zinc-400 font-mono text-[10px] mr-1">Ctrl+Space</kbd>
                            <span>Show autocomplete</span>
                          </li>
                          <li className="flex items-center">
                            <kbd className="px-1.5 py-0.5 bg-zinc-700 rounded text-zinc-400 font-mono text-[10px] mr-1">Ctrl+F</kbd>
                            <span>Search in editor</span>
                          </li>
                          <li className="flex items-center">
                            <kbd className="px-1.5 py-0.5 bg-zinc-700 rounded text-zinc-400 font-mono text-[10px] mr-1">Ctrl+Z</kbd>
                            <span>Undo</span>
                          </li>
                          <li className="flex items-center">
                            <kbd className="px-1.5 py-0.5 bg-zinc-700 rounded text-zinc-400 font-mono text-[10px] mr-1">Tab</kbd>
                            <span>Indent</span>
                          </li>
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
                <span className="text-xs text-zinc-400">Include <code className="bg-zinc-700 px-1.5 py-0.5 rounded">{"{{BACKENDS}}"}</code> placeholder</span>
              </div>
              <div className="border border-zinc-600 rounded-md overflow-hidden" style={customStyles.editorContainer}>
                <AceEditor
                  mode="haproxy"
                  theme="monokai"
                  name="content"
                  onChange={handleEditorChange}
                  value={formData.content}
                  width="100%"
                  height="100%"
                  fontSize={14}
                  showPrintMargin={false}
                  showGutter={true}
                  highlightActiveLine={true}
                  setOptions={editorOptions}
                  editorProps={{ $blockScrolling: Infinity }}
                  style={customStyles.editor}
                  className="ace-editor-custom haproxy-editor"
                />
              </div>
            </div>
          </div>

          <div className="border-t border-zinc-800 px-6 py-4 flex justify-end space-x-3 bg-zinc-900 sticky bottom-0">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 rounded-md text-sm font-medium text-zinc-300 bg-zinc-800 hover:bg-zinc-700 focus:outline-none"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
            >
              {template ? 'Update Template' : 'Create Template'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TemplateEditor;